import React from 'react'
import { useI18n } from '../../../i18n'
import listStyles from '../../../styles/pages/governance/GovernanceListing.module.scss'
import type { ControlCentricProgress } from '../../../services/controlCentricWorkflowApi'

type Props = {
  progress: ControlCentricProgress
}

export function JourneyCompactMetricsBar({ progress }: Props) {
  const { t } = useI18n()
  const repo = progress.evidenceRepositorySummary
  const total = progress.controlsTotal

  const controlTiles = [
    {
      key: 'completed',
      label: t('controlCentric.metrics.controlsCompleted', 'Controls completed'),
      value: `${progress.controlsCompleted}/${total}`,
      tone: listStyles.metricTileGreen,
      text: listStyles.textGreen,
    },
    {
      key: 'supported',
      label: t('controlCentric.metrics.evidenceSupported', 'Evidence-supported controls'),
      value: `${progress.controlsEvidenceSupported}/${total}`,
      tone: listStyles.metricTileBlue,
      text: listStyles.textBlue,
    },
    {
      key: 'gaps',
      label: t('controlCentric.metrics.controlsWithGaps', 'Controls with gaps'),
      value: `${progress.controlsWithGaps}/${total}`,
      tone: listStyles.metricTileYellow,
      text: listStyles.textYellow,
    },
    {
      key: 'partial',
      label: t('controlCentric.metrics.partialControls', 'Partial controls'),
      value: `${progress.partialControls}/${total}`,
      tone: listStyles.metricTileYellow,
      text: listStyles.textYellow,
    },
    {
      key: 'noncompliant',
      label: t('controlCentric.metrics.nonCompliantControls', 'Non-compliant controls'),
      value: `${progress.nonCompliantControls}/${total}`,
      tone: listStyles.metricTileRed,
      text: listStyles.textRed,
    },
  ]

  const evidenceTiles = repo
    ? [
        {
          key: 'artifacts',
          label: t('controlCentric.metrics.evidenceArtifacts', 'Evidence artifacts'),
          value: String(repo.totalArtifacts),
          tone: listStyles.metricTileBlue,
          text: listStyles.textBlue,
        },
        {
          key: 'valid',
          label: t('controlCentric.metrics.evidenceValid', 'Valid'),
          value: String(repo.validArtifacts),
          tone: listStyles.metricTileGreen,
          text: listStyles.textGreen,
        },
        {
          key: 'unverified',
          label: t('controlCentric.metrics.evidenceUnverified', 'Unverified'),
          value: String(repo.unverifiedArtifacts),
          tone: listStyles.metricTilePurple,
          text: listStyles.textPurple,
        },
      ]
    : []

  return (
    <section className={listStyles.panel}>
      <h3 className={listStyles.panelExternalTitle}>
        {t('controlCentric.metrics.sessionOverview', 'Session progress overview')}
      </h3>
      <p className="mb-3 text-xs text-slate-600">
        {t(
          'controlCentric.metrics.controlAxisHint',
          'Execution progress is measured by controls ({{total}}). Evidence repository totals are independent.',
          { total },
        )}
      </p>
      <div className={`${listStyles.metricsGrid} ${listStyles.metricsGrid5}`}>
        {controlTiles.map((tile) => (
          <div key={tile.key} className={`${listStyles.metricTile} ${tile.tone}`}>
            <p className={listStyles.metricTileLabel}>{tile.label}</p>
            <p className={`${listStyles.metricTileValue} ${tile.text}`}>{tile.value}</p>
          </div>
        ))}
      </div>
      {evidenceTiles.length ? (
        <>
          <h4 className={`${listStyles.panelExternalTitle} mt-4`}>
            {t('controlCentric.metrics.evidenceRepository', 'Evidence repository')}
          </h4>
          <div className={`${listStyles.metricsGrid} ${listStyles.metricsGrid5}`}>
            {evidenceTiles.map((tile) => (
              <div key={tile.key} className={`${listStyles.metricTile} ${tile.tone}`}>
                <p className={listStyles.metricTileLabel}>{tile.label}</p>
                <p className={`${listStyles.metricTileValue} ${tile.text}`}>{tile.value}</p>
              </div>
            ))}
          </div>
        </>
      ) : null}
    </section>
  )
}
